import { Button, Grid, MenuItem, TextField } from "@mui/material";
import { observer } from "mobx-react-lite";
import Head from "next/head";
import React from "react";
import config from "../../config";
import { ClientConfiguration } from "../../models/ClientConfiguration";
import { Order, PaymentMethod } from "../../models/Order";
import { PaymentGateway } from "../../models/PaymentGateway";
import {
    EcommercePayment,
    OfflinePayment,
    OrderAddress,
    PaymentMethodFriendlyName,
} from "../../requests/OrderRequests";
import AuthStore from "../../stores/AuthStore";
import CartStore from "../../stores/CartStore";
import PaymentProcessingStore from "../../stores/PaymentProcessingStore";
import ThemeStore from "../../stores/ThemeStore";
import { IFrameDecorder } from "../../utils/helpers";
import toast from "../../utils/toast";
import SquarePaymentForm from "./SquarePaymentForm";
import StripePaymentForm from "./StripePaymentForm";
import { Elements } from "@stripe/react-stripe-js";
import SquareDeviceStore from "../../stores/SquareDeviceStore";

type Props = {
    cartStore: CartStore;
    total: number;
    order: Order | null;
    address: OrderAddress;
    ecommercePayment: EcommercePayment;
    setEcommercePayment: React.Dispatch<React.SetStateAction<EcommercePayment>>;
    offlinePayment: OfflinePayment;
    setOfflinePayment: React.Dispatch<React.SetStateAction<OfflinePayment>>;
    onBack?: () => void;
    onSquareSubmit: (nonce: string, verificationToken?: string) => void;
    onStripeSubmit: () => Promise<string>;
    onStripeSuccess: () => void;
    onStripeError: (msg: string) => void;
    onIframeSubmit: () => void;
    loading: boolean;
    overridePurchaseText?: string;
};

const PaymentForm = observer(
    ({
        cartStore,
        total,
        order,
        address,
        ecommercePayment,
        setEcommercePayment,
        offlinePayment,
        setOfflinePayment,
        onSquareSubmit,
        onStripeSubmit,
        onStripeSuccess,
        onStripeError,
        onIframeSubmit,
        onBack,
        loading,
        overridePurchaseText,
    }: Props) => {
        const [iframeConfigured, setIframeConfigured] = React.useState(false);
        const [iframeHeight, setIframeHeight] = React.useState(1);
        const iframeRef = React.useRef<HTMLIFrameElement>(null);

        const setupIframe = (e: any) => {
            if (iframeConfigured) return;
            setIframeConfigured(true);

            iframeRef.current?.contentWindow?.postMessage(
                {
                    parentUrl: config.PUBLIC_URL || "http://localhost:3000",
                },
                "*",
            );
            iframeRef.current?.contentWindow?.postMessage(
                {
                    primaryColor: ThemeStore.primaryColor,
                    mode: ThemeStore.mode,
                },
                "*",
            );
        };

        const onStripeCheckout = async (e?: any): Promise<string> => {
            if (e && e.preventDefault) {
                e.preventDefault();
            }

            return onStripeSubmit();
        };

        React.useEffect(() => {
            const handleMessage = (e: MessageEvent<any>) => {
                if (e.data.type === "resize") {
                    setIframeHeight(e.data.height);
                } else if (e.data.type === "checkout") {
                    // Do checkout
                    toast.success("Order is being processed...");
                    onIframeSubmit();
                } else if (e.data.type === "ccform") {
                    // Update form values
                    const ecomPayment = IFrameDecorder(e.data.formData);

                    if (!ecomPayment) {
                        toast.error("Invalid payment form data");
                        return;
                    } else {
                        setEcommercePayment((payment: EcommercePayment) =>
                            Object.assign({}, payment, ecomPayment),
                        );
                    }
                } else if (e.data.type === "ready") {
                    iframeRef.current?.contentWindow?.postMessage(
                        {
                            primaryColor: ThemeStore.primaryColor,
                        },
                        "*",
                    );
                }
            };

            window.addEventListener("message", handleMessage);

            return () => {
                window.removeEventListener("message", handleMessage);
            };
        }, []);

        React.useEffect(() => {
            iframeRef.current?.contentWindow?.postMessage(
                {
                    primaryColor: ThemeStore.primaryColor,
                },
                "*",
            );
        }, [ThemeStore.primaryColor]);

        React.useEffect(() => {
            if (total === 0) return;
            PaymentProcessingStore.loadScripts(
                cartStore.cartKey!.key,
                cartStore.promoCode ?? "",
            );
        }, [cartStore.cart]);

        React.useEffect(() => {
            iframeRef.current?.contentWindow?.postMessage(
                {
                    loading,
                },
                "*",
            );
        }, [loading]);

        const boxofficeMode = AuthStore.loggedIn && AuthStore.boxoffice;

        return (
            <>
                {!boxofficeMode &&
                    !PaymentProcessingStore.usingCustomPaymentForm &&
                    total !== 0 && (
                        <div style={{ width: "100%", height: iframeHeight }}>
                            <iframe
                                ref={iframeRef}
                                src="/iframe/payment-form"
                                style={{ width: "100%", height: "100%" }}
                                onLoad={setupIframe}
                            />
                        </div>
                    )}
                {!boxofficeMode &&
                    PaymentProcessingStore.usingSquare &&
                    total !== 0 && (
                        <SquarePaymentForm
                            clientKey={cartStore.discriminator}
                            totalCost={total}
                            onPostalCodeChanged={(postalCode) => {
                                // TODO: Log postal code
                            }}
                            onSquareSubmit={onSquareSubmit}
                            address={address}
                            loading={loading}
                            currency={PaymentProcessingStore.currency}
                            gatewayId={PaymentProcessingStore.gatewayId}
                            overridePurchaseText={overridePurchaseText}
                        />
                    )}
                {!boxofficeMode &&
                    PaymentProcessingStore.usingStripe &&
                    total !== 0 && (
                        <Elements stripe={PaymentProcessingStore.stripePromise}>
                            <StripePaymentForm
                                loading={loading}
                                onStripeError={onStripeError}
                                onStripeSubmit={onStripeCheckout}
                                onStripeSuccess={onStripeSuccess}
                                total={total}
                            />
                        </Elements>
                    )}
                {(boxofficeMode || total === 0) && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        {boxofficeMode && (
                            <React.Fragment>
                                {total > 0 && (
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Square Terminal"
                                            variant="standard"
                                            name="squareDeviceId"
                                            fullWidth
                                            value={
                                                offlinePayment.squareDeviceId
                                            }
                                            onChange={(e) => {
                                                setOfflinePayment({
                                                    ...offlinePayment,
                                                    squareDeviceId: parseInt(
                                                        e.target.value,
                                                    ),
                                                });
                                            }}
                                            select
                                        >
                                            {SquareDeviceStore.devices
                                                .filter((d) => d.isActive)
                                                .map((device) => (
                                                    <MenuItem
                                                        value={device.id}
                                                        key={device.id}
                                                    >
                                                        {device.deviceName}
                                                    </MenuItem>
                                                ))}
                                        </TextField>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TextField
                                        label="Payment Type"
                                        variant="standard"
                                        name="paymentMethod"
                                        fullWidth
                                        required
                                        value={offlinePayment.paymentMethod}
                                        onChange={(e) => {
                                            setOfflinePayment({
                                                ...offlinePayment,
                                                paymentMethod: e.target
                                                    .value as PaymentMethod,
                                            });
                                        }}
                                        disabled={
                                            !!offlinePayment.squareDeviceId
                                        }
                                        select
                                    >
                                        {Object.keys(PaymentMethod).map((p) => (
                                            <MenuItem
                                                value={
                                                    (PaymentMethod as any)[p]
                                                }
                                                key={p}
                                            >
                                                {PaymentMethodFriendlyName(
                                                    (PaymentMethod as any)[p],
                                                )}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </React.Fragment>
                        )}
                        <Grid item xs={12}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={onIframeSubmit}
                                disabled={loading}
                            >
                                {overridePurchaseText ?? "Complete Purchase"}
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {onBack && (
                    <Button
                        onClick={onBack}
                        variant="contained"
                        color="secondary"
                        fullWidth
                        sx={{ mt: 2 }}
                        disabled={loading}
                    >
                        Back
                    </Button>
                )}
            </>
        );
    },
);

export default PaymentForm;
